/// <amd-module name="Build/SourceProviders/Bitbucket" />

import { BaseGit } from "Build/SourceProviders/BaseGit";
import { IBranchInfo, ICommitInfo, IRepositoryInfo, RepositoryTypes } from "Build/SourceProviders/Common";
import { ExternalUnknown } from "Build/SourceProviders/ExternalUnknown";
import * as Resources from "Build/SourceProviders/Resources";
import { IVCNavigationService } from "VC/Common/Navigation";

import { IVssPageContext } from "VSS/Platform/Context";
import { traceWarning } from "VSS/Platform/Trace";

import * as TraceNames from "TraceNames";

export class Bitbucket extends BaseGit {
    private _externalRepo: ExternalUnknown;
    private static s_BitbucketPrefix = "https://bitbucket.org";

    public constructor(pageContext: IVssPageContext) {
        super(pageContext);
        this._externalRepo = new ExternalUnknown(pageContext);
    }

    public getKey() {
        return RepositoryTypes.Bitbucket;
    }

    public getName(): string {
        return Resources.Bitbucket;
    }

    public getRepoIconName(): string {
        return "BitbucketLogo32";
    }

    public fetchRepositoryLink(info: IRepositoryInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getRepositoryLinkInternal(info.repositoryName));
    }

    public fetchBranchLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getBranchLinkInternal(info));
    }

    public fetchCommitLink(info: ICommitInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getCommitLinkInternal(info));
    }

    protected _getRepositoryLink(info: IRepositoryInfo, service: IVCNavigationService): string {
        return this._getRepositoryLinkInternal(info.repositoryName);
    }

    protected _getBranchLink(info: IBranchInfo, service: IVCNavigationService): string {
        return this._getBranchLinkInternal(info);
    }

    protected _getCommitLink(info: ICommitInfo, service: IVCNavigationService): string {
        return this._externalRepo.getCommitLink(info);
    }

    private _getRepositoryLinkInternal(repositoryName: string): string {
        if (!repositoryName) {
            console.error("Name is required to create a link to the repository");
            return "";
        }

        // We ran servicing to convert all repository IDs to orgname/repo format, but some used to be URLs
        // See if we still get any URLs here. If not, we can remove this block
        // Test with `indexOf` because clone URLs may start with https://username@bitbucket.org
        if (repositoryName.indexOf("bitbucket.org") > 0) {
            // repositoryName may contain EUII
            traceWarning(this.pageContext, TraceNames.area, "Bitbucket", "Data shape issue: repositoryName may be a URL");
        }

        // assume repositoryName is "user/repo"
        const maxParts = 3;
        const parts = repositoryName.split("/", maxParts);
        if (parts.length !== 2 || !parts[0] || !parts[1]) {
            console.error(`Repository name "${repositoryName}" is not in the expected format ("user/repository")`);
            return "";
        }

        return `${Bitbucket.s_BitbucketPrefix}/${repositoryName}`;
    }

    private _getBranchLinkInternal(info: IBranchInfo): string {
        if (info.repositoryName && info.branchName) {
            const normalizedBranchName = this._normalizeBranchName(info.branchName);
            return `${this._getRepositoryLinkInternal(info.repositoryName)}/branch/${normalizedBranchName}`;
        }
        return this._externalRepo.getBranchLink(info);
    }

    private _getCommitLinkInternal(info: ICommitInfo) {
        if (info.repositoryName && info.commitId) {
            return `${this._getRepositoryLinkInternal(info.repositoryName)}/commits/${info.commitId}`;
        }
        return this._externalRepo.fetchCommitLink(info);
    }
}